import React, { useEffect, useState } from 'react'
import '../common.css'
import { DataGrid } from '@mui/x-data-grid'
import { Select, MenuItem, InputLabel, FormControl, Typography, TextField } from '@mui/material'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import ImportButton from '../importButton/importButton'
import ExportButton from '../exportButton/exportButton'
import { filterDataByBrand } from '../utils/filters'

import { apiUrl } from '../../../environments/environments'
import Warning from '@mui/icons-material/Warning'
import SyncPriceButton from '../../global/SyncPriceButton'
import { useIsProjectEditor } from '../../../hooks/useUserPermissions'

const Header = ({ title, onFilter, field, filterValue, id }) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant='caption' style={{ fontSize: '1rem' }}>
            {title}
        </Typography>

        <TextField
            variant='standard'
            value={filterValue || ''}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onFilter(field, e.target.value)}
            style={{ width: '100%' }}
            inputProps={{ id }}
        />
    </div>
)

function RooftopsOptionsPage() {
    const [data, setData] = useState([])
    const [dataToDisplay, setDataToDisplay] = useState([])
    const [brandFilter, setBrandFilter] = useState('All')
    const [errorMessage, setErrorMessage] = useState(false)
    const [loading, setLoading] = useState(true)
    const [filters, setFilters] = useState({})
    const isProjectEditor = useIsProjectEditor()

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }))
    }

    const refreshData = () => {
        setLoading(true)
        ApiService(`${apiUrl}/rooftop/options/`).then(({ status, data }) => {
            if (status === 200 && data) {
                setData(data.result)
            }
            setLoading(false)
        })
    }

    // Get data from database

    useEffect(() => {
        refreshData()
    }, [])

    // filter font side on brand
    useEffect(() => {
        let filteredData = filterDataByBrand(data, brandFilter)

        const isEmpty = (v) => v == null || v === ''

        const isNumber = (raw) => {
            const s = String(raw).trim()
            return /^-?\d+(\.\d+)?$/.test(s) // -1806, 1806, -12.5, 12.5
        }

        // Optionnel mais recommandé: enlève espaces/devise + normalise tirets unicode
        const normalizePrice = (v) =>
            String(v ?? '')
                .trim()
                .replace(/\u2212|\u2013|\u2014/g, '-') // − – — => -
                .replace(/\s+/g, '') // supprime espaces
                .replace(/[€$,]/g, '') // supprime symboles courants (si tu en as)

        const isNegativePriceString = (v) => {
            const p = normalizePrice(v)
            // vrai négatif = contient "-<digit>" (ex: -1806, -0.5)
            return /-\d/.test(p)
        }

        Object.entries(filters).forEach(([field, value]) => {
            const raw = value == null ? '' : String(value).trim()

            // ✅ vide => pas de filtre (retour arrière)
            if (raw === '') return
            filteredData = filteredData.filter((row) => {
                const fieldValue = row[field]

                // ----- PRICE LOGIC -----
                if (field === 'price') {
                    // "-" seul => afficher les prix négatifs
                    if (raw === '-') {
                        return isNegativePriceString(fieldValue)
                    }

                    // nombre valide => filtre normal (sur la string)
                    if (isNumber(raw)) {
                        return normalizePrice(fieldValue).includes(normalizePrice(raw))
                    }

                    // texte non numérique => no price
                    return isEmpty(fieldValue)
                }

                // ----- OTHER COLUMNS -----
                if (isEmpty(fieldValue)) return false
                return String(fieldValue).toLowerCase().includes(raw.toLowerCase())
            })
        })

        setDataToDisplay(filteredData)
    }, [brandFilter, data, filters])

    const uploadStart = () => {
        setLoading(true)
    }

    const uploadSuccess = (data) => {
        setData(data)
        setLoading(false)
    }

    const uploadError = (error) => {
        setLoading(false)
        setErrorMessage(error)
    }

    const columns = [
        {
            field: 'codeModel',
            headerName: 'Model',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Model'
                    onFilter={handleFilterChange}
                    field='codeModel'
                    filterValue={filters.codeModel}
                    id='filter-options-model'
                />
            ),
        },

        {
            field: 'config',
            headerName: 'Assembly',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Assembly'
                    onFilter={handleFilterChange}
                    field='config'
                    filterValue={filters.config}
                    id='filter-options-assembly'
                />
            ),
        },

        {
            field: 'label',
            headerName: 'Label',
            flex: 1,
            minWidth: 200,
            renderHeader: () => (
                <Header
                    title='Label'
                    onFilter={handleFilterChange}
                    field='label'
                    filterValue={filters.label}
                    id='filter-options-label'
                />
            ),
        },

        {
            field: 'value',
            headerName: 'Value',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Value'
                    onFilter={handleFilterChange}
                    field='value'
                    filterValue={filters.value}
                    id='filter-options-value'
                />
            ),
        },

        {
            field: 'type',
            headerName: 'Type',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Type'
                    onFilter={handleFilterChange}
                    field='type'
                    filterValue={filters.type}
                    id='filter-options-type'
                />
            ),
        },

        {
            field: 'oldPrice',
            headerName: 'Old price',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Old price'
                    onFilter={handleFilterChange}
                    field='oldPrice'
                    filterValue={filters.oldPrice}
                    id='filter-options-oldprice'
                />
            ),
        },
        {
            field: 'varcond',
            headerName: 'Varconds',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Varconds'
                    onFilter={handleFilterChange}
                    field='varcond'
                    filterValue={filters.varcond}
                    id='filter-options-varconds'
                />
            ),
        },
        {
            field: 'kmat',
            headerName: 'Kmat',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Kmat'
                    onFilter={handleFilterChange}
                    field='kmat'
                    filterValue={filters.kmat}
                    id='filter-options-kmat'
                />
            ),
        },
        {
            field: 'price',
            headerName: 'Price',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                if (params.value === undefined || params.value === null) {
                    return (
                        <span style={{ color: 'red', background: 'white' }}>
                            <Warning color='error' className='warningIcon'></Warning> No Price
                        </span>
                    )
                }
                return params.value
            },
            renderHeader: () => (
                <Header
                    title='Price'
                    onFilter={handleFilterChange}
                    field='price'
                    filterValue={filters.price}
                    id='filter-options-price'
                />
            ),
        },
    ]

    return (
        <div className='page-container'>
            <div className='actions'>
                <div>
                    <FormControl variant='outlined' className='select'>
                        <InputLabel id='demo-simple-select-outlined-label'>Brand</InputLabel>

                        <Select
                            labelId='demo-simple-select-outlined-label'
                            id='demo-simple-select-outlined'
                            value={brandFilter}
                            onChange={(e) => setBrandFilter(e.target.value)}
                            label='Brand'>
                            <MenuItem value='All'>All brands</MenuItem>
                            <MenuItem value='Carrier'>Carrier</MenuItem>
                            <MenuItem value='Ciat'>Ciat</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                {isProjectEditor && (
                    <>
                        <div>
                            <ImportButton
                                api='/rooftop/options/'
                                onSuccess={uploadSuccess}
                                onError={uploadError}
                                onStart={uploadStart}
                            />
                        </div>
                        <div>
                            <ExportButton
                                id='download-prices-options'
                                data={data}
                                columns={columns}
                                fileName='Options_Prices.xlsx'
                                sheetName='Options_Prices'
                            />
                        </div>
                        <div>
                            <SyncPriceButton
                                builder={'rooftops'}
                                onSyncComplete={refreshData}
                                initialMessage='Sync all rooftops prices'
                            />
                        </div>
                        <div>
                            {loading && <span> LOADING .... </span>}
                            {errorMessage && (
                                <>
                                    import error: <b>{errorMessage}</b>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className='table-container' style={{ marginBottom: '20px' }}>
                <Typography variant='h6' component='h2' gutterBottom>
                    Options prices
                </Typography>

                <DataGrid
                    autoHeight={false}
                    style={{
                        backgroundColor: 'white',
                        width: '100%',
                        fontSize: '1rem',
                        height: '100%',
                        '.MuiDataGridOverlayWrapper': {
                            minHeight: '100px',
                        },
                    }}
                    rows={dataToDisplay}
                    columns={columns}
                    getRowId={(row) =>
                        `${row.codeModel}-${row.config}-${row.label}-${row.value}-${row.type}-${row.kmat}-${row.varcond}-${row.price}`
                    }
                    loading={loading}
                    pageSizeOptions={[5, 10, 20]}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 10 },
                        },
                    }}
                    rowHeight={70}
                />
            </div>
        </div>
    )
}

export default RooftopsOptionsPage
